import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Loader } from "@shared/ui/Loader";
import { Dialog } from "@shared/ui/Dialog";
import { getValidTimeslotsFromContiguousTimeslots } from "@platform/features/timeslots/utils/getValidTimeslotsFromContinguousTimeslots";
import { TimezoneOption } from "@platform/features/timezones/timezones.types";
import { useTimezoneOptions } from "@platform/hooks/useTimezoneOptions";
import Scheduling from "../Scheduling/Scheduling";
import { useScheduling } from "../../hooks/useScheduling";
import { useSubmitSchedulingAvailability } from "../../hooks/useSubmitSchedulingAvailability";
import { useGetSchedulingAvailability } from "../../hooks/useGetSchedulingAvailability";

export type Props = {
  onDone: () => void;
  onClose: () => void;
  isOpen: boolean;
  programId: number;
  userPhaseActionId: number;
};

export function SchedulingDialog({
  onDone,
  onClose,
  isOpen,
  programId,
  userPhaseActionId,
}: Props) {
  const navigate = useNavigate();

  // client timezone reading and updating logic
  const { timezones, selectedTimezone, handleTimezoneChange } =
    useTimezoneOptions();

  // query api for saved time slots
  const {
    data: savedTimeSlots,
    isLoading,
    isError,
    refetch,
  } = useGetSchedulingAvailability(programId);

  // scheduling slot selection and grouping logic
  const {
    contiguousTimeslots,
    timeSlots,
    selectedTimeSlots,
    handleMouseDown,
    handleMouseOver,
    handleTouchStart,
    handleTouchMove,
    handleTouchEnd,
    validHoursCount,
    requirementsMet,
  } = useScheduling({
    savedTimeSlots: savedTimeSlots || [],
    selectedTimezone: selectedTimezone || ({} as TimezoneOption),
  });

  // mutation to submit scheduling availability to api
  const submitSchedulingAvailability = useSubmitSchedulingAvailability(
    programId,
    userPhaseActionId,
    () => {
      onDone();
      refetch();
    }
  );

  // submission mutation state for ux feedback
  const { isPending, isError: isSubmitError } = submitSchedulingAvailability;

  // navifate to error page if there is an error
  useEffect(() => {
    if (isError || isSubmitError) {
      navigate("/error");
    }
  }, [isError, isSubmitError, navigate]);

  // submit handler cleans up the contiguous slots and submits to api
  const handleSubmit = () => {
    const validTimeSlots =
      getValidTimeslotsFromContiguousTimeslots(contiguousTimeslots);

    submitSchedulingAvailability.mutateAsync(validTimeSlots);
  };

  return (
    <Dialog
      title="Weekly Availability"
      description="Your participation in Pollinator is not confirmed until you choose at least 10 hours’ worth of time slots when you will be available to meet with your peer circle this fall. To ensure you’re successfully matched, we recommend choosing as many time slots as possible."
      isOpen={isOpen}
      onClose={onClose}
      className="py-0"
      containerClassName="gap-0"
      primaryButtonText="Save"
      secondaryButtonText="Cancel"
      onPrimaryClick={handleSubmit}
      onSecondaryClick={onClose}
      primaryButtonProps={{
        disabled: !requirementsMet || isPending,
      }}
      secondaryButtonProps={{
        disabled: isPending,
      }}
      size="5xl"
    >
      {isPending && <Loader isAbsolute />}
      <Scheduling
        timeZoneOptions={timezones}
        selectedTimezone={selectedTimezone || ({} as TimezoneOption)}
        onTimezoneChange={handleTimezoneChange}
        isSubmitting={isPending}
        isLoading={isLoading}
        timeSlots={timeSlots}
        selectedTimeSlots={selectedTimeSlots}
        contiguousTimeslots={contiguousTimeslots}
        handleMouseDown={handleMouseDown}
        handleMouseOver={handleMouseOver}
        handleTouchStart={handleTouchStart}
        handleTouchMove={handleTouchMove}
        handleTouchEnd={handleTouchEnd}
        validHoursCount={validHoursCount}
      />
    </Dialog>
  );
}

export default SchedulingDialog;
